import { Logger } from '@streem/logger';
import { observable, action, computed, runInAction, reaction, IReactionDisposer } from 'mobx';
import { BrowserPermission } from '../stores/browser_permission_store';
import appLogger from '../util/logging/app_logger';

export class DeviceStore {
    @observable
    private _audioOutputDevices: MediaDeviceInfo[] = [];

    @observable
    private _audioInputDevices: MediaDeviceInfo[] = [];

    @observable
    private _videoDevices: MediaDeviceInfo[] = [];

    @observable
    public hasVideoPermission = false;

    @observable
    public hasAudioPermission = false;

    protected logger: Logger;

    private videoPermissonDisposer: IReactionDisposer;
    private audioPermissonDisposer: IReactionDisposer;

    private audioBrowserPermission: BrowserPermission;
    private videoBrowserPermission: BrowserPermission;

    public initialize(): void {
        this.audioBrowserPermission.initialize();
        this.videoBrowserPermission.initialize();

        this.videoPermissonDisposer = reaction(
            () => this.videoBrowserPermission.hasPermissionGranted,
            () => this.getVideoDevices(),
        );

        this.audioPermissonDisposer = reaction(
            () => this.audioBrowserPermission.hasPermissionGranted,
            () => this.getAudioDevices(),
        );
    }

    constructor() {
        this.logger = appLogger.extend('Device Store');
        navigator.mediaDevices.addEventListener('devicechange', this.deviceChangeHandler);

        this.audioBrowserPermission = new BrowserPermission({
            constraints: { audio: true },
            queryName: 'microphone',
        });
        this.videoBrowserPermission = new BrowserPermission({
            constraints: { video: true },
            queryName: 'camera',
        });
    }

    public dispose(): void {
        navigator.mediaDevices.removeEventListener('devicechange', this.deviceChangeHandler);
        this.videoPermissonDisposer();
        this.audioPermissonDisposer();
    }

    private getAudioDevices(): void {
        getDevices(this.audioBrowserPermission, 'audioinput').then(devices => {
            runInAction(() => {
                this.logger.info('Updating audioinput device list', devices);
                this._audioInputDevices = devices;
                this.hasAudioPermission = this.audioBrowserPermission.hasPermissionGranted;
            });
        });

        getDevices(this.audioBrowserPermission, 'audiooutput').then(devices => {
            runInAction(() => {
                this.logger.info('Updating audiooutput device list', devices);
                this._audioOutputDevices = devices;
            });
        });
    }

    private getVideoDevices(): void {
        getDevices(this.videoBrowserPermission, 'videoinput').then(devices => {
            const video = devices.filter(d => d.kind === 'videoinput');
            runInAction(() => {
                this.logger.info('Updating videoinput device list', devices);
                this._videoDevices = video;
                this.hasVideoPermission = this.videoBrowserPermission.hasPermissionGranted;
            });
        });
    }

    @computed
    public get audioInputDevices(): MediaDeviceInfo[] {
        return this._audioInputDevices;
    }

    @computed
    public get audioOutputDevices(): MediaDeviceInfo[] {
        return this._audioOutputDevices;
    }

    @computed
    public get videoDevices(): MediaDeviceInfo[] {
        return this._videoDevices;
    }

    @action.bound
    private async deviceChangeHandler(): Promise<void> {
        this.logger.info('Device change detected...');
        const devices = await navigator.mediaDevices.enumerateDevices();
        const input = devices.filter(d => d.kind === 'audioinput');
        const output = devices.filter(d => d.kind === 'audiooutput');
        const video = devices.filter(d => d.kind === 'videoinput');

        this._audioInputDevices = input;
        this._audioOutputDevices = output;
        this._videoDevices = video;
    }
}

const getDevices = async (
    browserPermission: BrowserPermission,
    kind: MediaDeviceKind,
): Promise<MediaDeviceInfo[]> => {
    let devices = await navigator.mediaDevices.enumerateDevices();

    const shouldPromptForBrowserPermission = devices.some(
        device => device.kind === kind && device.label === '',
    );

    if (shouldPromptForBrowserPermission) {
        const granted = await browserPermission.prompt();
        if (granted) {
            devices = await navigator.mediaDevices.enumerateDevices();
        }
    }

    return devices.filter(device => device.label !== '' && device.kind === kind);
};

let store: DeviceStore | undefined = undefined;

export function getDeviceStore(): DeviceStore {
    if (store == null) {
        store = new DeviceStore();
    }
    return store;
}
